/**
 * annotationStore — every write to `annotations` goes through here, so the
 * per-sense usage counts stay in step with the annotations themselves.
 *
 * For each yasna sense, `senseStats/{language}/senses/{senseId}` holds
 * `annotationCount`: how many annotations link to it. Each create, delete
 * and meaning change updates the count in the same atomic batch as the
 * annotation. topSenses() ranks senses by it (the most-annotated words,
 * verbs and expressions), which is what hoja caches (see the specs,
 * Requirement 30).
 *
 * Annotations from before yasna (entryId, no senseId) are not counted.
 */
import {
  collection, doc, writeBatch, increment, serverTimestamp,
  query, orderBy, limit, getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';
import { languageOfSense } from '../yasna/loadYasna';

// Title annotations — words of the video's title, shown to students for the
// whole video — are anchored to the title rather than a subtitle or a time:
// subtitleId is this marker, and they have no start or end time. Queries for
// subtitle-free annotations (subtitleId == null) leave them out.
export const TITLE_ANCHOR = '__title__';
export const isTitleAnnotation = (a) => !!a && a.subtitleId === TITLE_ANCHOR;

export const statRef = (senseId, language = languageOfSense(senseId)) =>
  doc(db, 'senseStats', language, 'senses', senseId);

// Add `delta` to a sense's count, creating its stats document if needed.
function bump(batch, senseId, language, delta) {
  if (!senseId || !delta) return;
  const lang = language || languageOfSense(senseId);
  batch.set(statRef(senseId, lang), {
    senseId,
    language: lang,
    annotationCount: increment(delta),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

/** Save a new annotation; returns it with its id. */
export async function createAnnotation(data) {
  const ref = doc(collection(db, 'annotations'));
  const batch = writeBatch(db);
  batch.set(ref, data);
  bump(batch, data.senseId, data.language, 1);
  await batch.commit();
  return { id: ref.id, ...data };
}

/** Apply `changes` to an existing annotation, moving the count if its sense changed. */
export async function updateAnnotation(annotation, changes) {
  const batch = writeBatch(db);
  batch.update(doc(db, 'annotations', annotation.id), changes);
  if (changes.senseId && changes.senseId !== annotation.senseId) {
    bump(batch, annotation.senseId, annotation.language, -1);
    bump(batch, changes.senseId, changes.language, 1);
  }
  await batch.commit();
  return { ...annotation, ...changes };
}

/**
 * Save the words of one subtitle (or the title) in this order: each gets
 * its place as `order` (Requirement 58). Only those whose place changed are
 * written. Resolves to the list with the new orders.
 */
export async function saveAnnotationOrder(list) {
  const placed = list.map((a, order) => ({ ...a, order }));
  const changed = placed.filter((a, i) => list[i].order !== i);
  if (changed.length > 0) {
    const batch = writeBatch(db);
    changed.forEach((a) => batch.update(doc(db, 'annotations', a.id), { order: a.order }));
    await batch.commit();
  }
  return placed;
}

/** Count changes for deleting these annotations, added to an existing batch. */
export function addAnnotationDeletes(batch, annotations) {
  annotations.forEach((a) => {
    batch.delete(doc(db, 'annotations', a.id));
    bump(batch, a.senseId, a.language, -1);
  });
}

export async function deleteAnnotation(annotation) {
  const batch = writeBatch(db);
  addAnnotationDeletes(batch, [annotation]);
  await batch.commit();
}

/**
 * The `n` most-annotated senses in a language, most used first:
 * [{ senseId, language, annotationCount }]. One read per sense returned.
 */
export async function topSenses(language, n = 1000) {
  const snap = await getDocs(query(
    collection(db, 'senseStats', language, 'senses'),
    orderBy('annotationCount', 'desc'),
    limit(n)
  ));
  return snap.docs.map((d) => d.data()).filter((s) => s.annotationCount > 0);
}
